import React, { useContext, useEffect, useState } from 'react';
import Select from 'react-select'; // Assuming this external library path is correct

// Corrected import paths based on your structure
import AppBreadCrumb from '../../common/AppBreadCrumb';
import { createParty, fetchParty, updateParty, getParties } from '../../../api/PartiesAPI'; // Corrected API path
import { getPartygroups } from '../../../api/PartygroupsAPI '; // Corrected API path (assuming PartyGroupsAPI exists)
import { handleApiError } from '../../../api/risapi'; // Corrected API path
import AppToaster from '../../AppToaster'; // Corrected component path
import { useNavigate } from 'react-router-dom';
import { capitalizeFirstLetter } from '../../../commonfunctions'; // Corrected common functions path

const PartiesForm = ({ formType, currentPartyId }) => {
    const navigate = useNavigate();
    const [currentParty, setCurrentParty] = useState({
        // Initial state with addresses as an empty array
        name: '',
        code: '',
        grp: null,
        pan: '',
        phone_number: '',
        email: '',
        notify_by_whatsapp: false,
        notify_by_sms: false,
        notify_by_email: false,
        addresses: [], // Initialize as empty array
    });
    const [currentPartyError, setCurrentPartyError] = useState({});
    const [partyGroups, setPartyGroups] = useState([]);
    const [partyNumbering, setPartyNumbering] = useState('auto');

    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});
    const [loading, setLoading] = useState(false);

    // --- Fetch Party Groups ---
    useEffect(() => {
        setLoading(true);
        // Ensure getPartyGroups exists in PartyGroupsAPI.js and path is correct
        getPartygroups({ paginate: false }, (response) => {
            setLoading(false);
            if (response.partygroups) {
                if (Array.isArray(response.partygroups)) {
                    setPartyGroups(response.partygroups.map(pg => ({ value: pg.id, label: pg.name })));
                } else {
                    console.error("Party groups response is not an array:", response.partygroups);
                    setPartyGroups([]);
                    setToaster({ type: 'danger', heading: 'Error', message: 'Could not load party groups (invalid data).' });
                    setShowToaster(true);
                }
            } else {
                console.error("Error fetching party groups");
                setPartyGroups([]);
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not load party groups.' });
                setShowToaster(true);
            }
        });
    }, []);

    // --- Fetch Party Data / Settings ---
    useEffect(() => {
        setCurrentParty({ // Reset form
            name: '', code: '', grp: null, pan: '', phone_number: '', email: '',
            notify_by_whatsapp: false, notify_by_sms: false, notify_by_email: false,
            addresses: [],
        });
        setCurrentPartyError({});
        setLoading(true);

        if (formType === 'edit' && currentPartyId) {
            fetchParty(currentPartyId, (response) => {
                setLoading(false);
                if (response.party) {
                    const fetchedParty = response.party;
                    const addressesFromServer = fetchedParty.addresses;
                    const formattedAddresses = Array.isArray(addressesFromServer)
                        ? addressesFromServer.map(addr => ({
                            ...addr,
                            is_default_billing: !!addr.is_default_billing,
                            is_default_shipping: !!addr.is_default_shipping,
                        }))
                        : [];
                    setCurrentParty({
                        ...fetchedParty,
                        grp: fetchedParty.partygroup?.id || null,
                        addresses: formattedAddresses,
                    });
                    setPartyNumbering(response.party_numbering || 'auto');
                } else {
                    handleApiError(response, setCurrentPartyError);
                    setToaster({ type: 'danger', heading: 'Error', message: 'Could not load party data.' });
                    setShowToaster(true);
                }
            });
        } else if (formType === 'create') {
            getParties({ paginate: false, limit: 1 }, (response) => {
                setLoading(false);
                setPartyNumbering(response.party_numbering || 'auto');
            });
        } else {
            setLoading(false);
        }
    }, [formType, currentPartyId]);

    // --- Input Handlers ---
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCurrentParty(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handlePartyGroupChange = (selectedOption) => {
        setCurrentParty(prev => ({ ...prev, grp: selectedOption ? selectedOption.value : null }));
    };

    // --- Address Handlers ---
    const addAddress = () => {
        setCurrentParty(prev => ({
            ...prev,
            addresses: [
                ...(Array.isArray(prev.addresses) ? prev.addresses : []),
                { id: null, address_type: 'general', address_line_1: '', address_line_2: '', city: '', state: '', pincode: '', gstin: '', is_default_billing: false, is_default_shipping: false },
            ],
        }));
    };

    const removeAddress = (index) => {
        setCurrentParty(prev => ({
            ...prev,
            addresses: (Array.isArray(prev.addresses) ? prev.addresses : []).filter((_, i) => i !== index),
        }));
    };

    const handleAddressChange = (index, e) => {
        const { name, value, type, checked } = e.target;
        setCurrentParty(prev => {
            const currentAddresses = Array.isArray(prev.addresses) ? prev.addresses : [];
            const newAddresses = [...currentAddresses];
            if (!newAddresses[index]) return prev;

            const updatedAddress = { ...newAddresses[index], [name]: type === 'checkbox' ? checked : value };

            if (name === 'is_default_billing' && checked) newAddresses.forEach((addr, i) => { if (i !== index) addr.is_default_billing = false; });
            if (name === 'is_default_shipping' && checked) newAddresses.forEach((addr, i) => { if (i !== index) addr.is_default_shipping = false; });

            newAddresses[index] = updatedAddress;
            return { ...prev, addresses: newAddresses };
        });
    };

    // --- Save Handlers ---
    const saveForm = (exitForm) => {
        setCurrentPartyError({});
        setLoading(true);
        const apiCall = formType === 'create' ? createParty : updateParty;
        const dataToSend = { ...currentParty, grp: currentParty.grp };

        apiCall(dataToSend, (response) => {
            setLoading(false);
            if (response.party) {
                if (exitForm) {
                    navigate('/franchisee_user/masters/parties', { state: { message: `Party ${formType === 'create' ? 'saved' : 'updated'} successfully!` } });
                } else if (formType === 'create') {
                    setCurrentParty({ name: '', code: '', grp: null, pan: '', phone_number: '', email: '', notify_by_whatsapp: false, notify_by_sms: false, notify_by_email: false, addresses: [] });
                    setToaster({ type: 'success', heading: 'Success', message: 'Party added successfully' });
                    setShowToaster(true);
                } else if (formType === 'edit' && !exitForm) {
                    setToaster({ type: 'success', heading: 'Success', message: 'Party updated successfully' });
                    setShowToaster(true);
                }
            } else {
                handleApiError(response, setCurrentPartyError);
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not save party. Please check errors.' });
                setShowToaster(true);
            }
        });
    };

    // --- Reset Handler ---
    const handleReset = () => {
        if (formType === 'create') {
            setCurrentParty({ name: '', code: '', grp: null, pan: '', phone_number: '', email: '', notify_by_whatsapp: false, notify_by_sms: false, notify_by_email: false, addresses: [] });
            setCurrentPartyError({});
        } else {
            setLoading(true);
            fetchParty(currentPartyId, (response) => {
                setLoading(false);
                if (response.party) {
                    const fetchedParty = response.party;
                    const addressesFromServer = fetchedParty.addresses;
                    const formattedAddresses = Array.isArray(addressesFromServer) ? addressesFromServer.map(addr => ({ ...addr, is_default_billing: !!addr.is_default_billing, is_default_shipping: !!addr.is_default_shipping })) : [];
                    setCurrentParty({ ...fetchedParty, grp: fetchedParty.partygroup?.id || null, addresses: formattedAddresses });
                    setPartyNumbering(response.party_numbering || 'auto');
                    setCurrentPartyError({});
                } else {
                    setToaster({ type: 'danger', heading: 'Error', message: 'Could not reload party data.' });
                    setShowToaster(true);
                }
            });
        }
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    {/* Breadcrumb with corrected path */}
                    <AppBreadCrumb title={`${capitalizeFirstLetter(formType)} Party`}>
                        <li><a className="f-s-14 f-w-500" href="#">Masters</a></li>
                        <li><a className="f-s-14 f-w-500" href="/franchisee_user/masters/parties">Parties</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">{capitalizeFirstLetter(formType)}</a></li>
                    </AppBreadCrumb>

                    {loading && (
                        <div className="loader_box"><div className="loader_14"></div></div>
                    )}

                    {!loading && (
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h5>{formType === 'create' ? 'Add New' : 'Edit'} Party {formType === 'edit' ? `: ${currentParty?.name || ''}` : ''}</h5>
                                    </div>
                                    <div className="card-body">
                                        <form className="app-form" onSubmit={(e) => e.preventDefault()}>
                                            {/* Party Details Section */}
                                            <h6 className="mb-3">Party Details</h6>
                                            <div className="row mb-3">
                                                {/* Name */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Name *</label>
                                                    <input className="form-control" name="name" value={currentParty.name || ''} onChange={handleInputChange} required />
                                                    <span className="text-danger">{currentPartyError?.name}</span>
                                                </div>
                                                {/* Code (Conditional) */}
                                                {partyNumbering === 'manual' && (
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Code *</label>
                                                        <input className="form-control" name="code" value={currentParty.code || ''} onChange={handleInputChange} required />
                                                        <span className="text-danger">{currentPartyError?.code}</span>
                                                    </div>
                                                )}
                                                {partyNumbering === 'auto' && formType === 'edit' && (
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Code</label>
                                                        <input className="form-control" value={currentParty.code || ''} readOnly disabled />
                                                    </div>
                                                )}
                                                {/* Party Group */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Party Group *</label>
                                                    <Select
                                                        options={partyGroups}
                                                        value={partyGroups.find(option => option.value === currentParty.grp)}
                                                        onChange={handlePartyGroupChange}
                                                        placeholder="Select Party Group"
                                                        isClearable
                                                    />
                                                    <span className="text-danger">{currentPartyError?.grp}</span>
                                                </div>
                                                {/* PAN */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">PAN</label>
                                                    <input className="form-control text-uppercase" name="pan" value={currentParty.pan || ''} onChange={handleInputChange} maxLength="10" />
                                                    <span className="text-danger">{currentPartyError?.pan}</span>
                                                </div>
                                                {/* Phone */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Phone Number</label>
                                                    <input className="form-control" name="phone_number" value={currentParty.phone_number || ''} onChange={handleInputChange} />
                                                    <span className="text-danger">{currentPartyError?.phone_number}</span>
                                                </div>
                                                {/* Email */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Email</label>
                                                    <input type="email" className="form-control" name="email" value={currentParty.email || ''} onChange={handleInputChange} />
                                                    <span className="text-danger">{currentPartyError?.email}</span>
                                                </div>
                                            </div>

                                            {/* Notification Preferences */}
                                            <h6 className="mt-4 mb-3">Notification Preferences</h6>
                                            <div className="row mb-3">
                                                <div className="col-auto">
                                                    <div className="form-check form-switch">
                                                        <input className="form-check-input" type="checkbox" role="switch" id="notify_whatsapp" name="notify_by_whatsapp" checked={currentParty.notify_by_whatsapp || false} onChange={handleInputChange} />
                                                        <label className="form-check-label" htmlFor="notify_whatsapp">WhatsApp</label>
                                                    </div>
                                                </div>
                                                <div className="col-auto">
                                                    <div className="form-check form-switch">
                                                        <input className="form-check-input" type="checkbox" role="switch" id="notify_sms" name="notify_by_sms" checked={currentParty.notify_by_sms || false} onChange={handleInputChange} />
                                                        <label className="form-check-label" htmlFor="notify_sms">SMS</label>
                                                    </div>
                                                </div>
                                                <div className="col-auto">
                                                    <div className="form-check form-switch">
                                                        <input className="form-check-input" type="checkbox" role="switch" id="notify_email" name="notify_by_email" checked={currentParty.notify_by_email || false} onChange={handleInputChange} />
                                                        <label className="form-check-label" htmlFor="notify_email">Email</label>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Addresses Section */}
                                            <hr />
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="mb-0">Addresses</h6>
                                                <button type="button" className="btn btn-sm btn-facebook text-white" onClick={addAddress}><i className="ti ti-plus me-1"></i> Add Address</button>
                                            </div>
                                            <span className="text-danger d-block mb-2">{currentPartyError?.addresses && typeof currentPartyError.addresses === 'string' ? currentPartyError.addresses : ''}</span>

                                            {/* Address Mapping */}
                                            {currentParty && Array.isArray(currentParty.addresses) && currentParty.addresses.map((address, index) => (
                                                <div key={address.id || `addr-${index}`} className="address-block border p-3 mb-3 rounded position-relative">
                                                    <button type="button" className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-1" onClick={() => removeAddress(index)} style={{ lineHeight: 1, padding: '0.2rem 0.4rem' }} title="Remove Address">&times;</button>
                                                    <div className="row">
                                                        {/* Address Type */}
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">Address Type *</label>
                                                            <select className="form-select" name="address_type" value={address.address_type || 'general'} onChange={e => handleAddressChange(index, e)}>
                                                                <option value="general">General</option>
                                                                <option value="billing">Billing</option>
                                                                <option value="shipping">Shipping</option>
                                                            </select>
                                                            <span className="text-danger">{currentPartyError?.addresses?.[index]?.address_type}</span>
                                                        </div>
                                                        {/* GSTIN */}
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">GSTIN</label>
                                                            <input className="form-control text-uppercase" name="gstin" value={address.gstin || ''} onChange={e => handleAddressChange(index, e)} maxLength="15" />
                                                            <span className="text-danger">{currentPartyError?.addresses?.[index]?.gstin}</span>
                                                        </div>
                                                        {/* Default Flags */}
                                                        <div className="col-md-4 mb-3 d-flex align-items-end flex-wrap">
                                                            <div className="form-check form-switch me-3 mb-1">
                                                                <input className="form-check-input" type="checkbox" role="switch" id={`is_default_billing_${index}`} name="is_default_billing" checked={address.is_default_billing || false} onChange={e => handleAddressChange(index, e)} />
                                                                <label className="form-check-label" htmlFor={`is_default_billing_${index}`}>Default Billing</label>
                                                            </div>
                                                            <div className="form-check form-switch mb-1">
                                                                <input className="form-check-input" type="checkbox" role="switch" id={`is_default_shipping_${index}`} name="is_default_shipping" checked={address.is_default_shipping || false} onChange={e => handleAddressChange(index, e)} />
                                                                <label className="form-check-label" htmlFor={`is_default_shipping_${index}`}>Default Shipping</label>
                                                            </div>
                                                        </div>
                                                        {/* Address Line 1 */}
                                                        <div className="col-12 mb-3">
                                                            <label className="form-label">Address Line 1 *</label>
                                                            <input className="form-control" name="address_line_1" value={address.address_line_1 || ''} onChange={e => handleAddressChange(index, e)} required />
                                                            <span className="text-danger">{currentPartyError?.addresses?.[index]?.address_line_1}</span>
                                                        </div>
                                                        {/* Address Line 2 */}
                                                        <div className="col-12 mb-3">
                                                            <label className="form-label">Address Line 2</label>
                                                            <input className="form-control" name="address_line_2" value={address.address_line_2 || ''} onChange={e => handleAddressChange(index, e)} />
                                                            <span className="text-danger">{currentPartyError?.addresses?.[index]?.address_line_2}</span>
                                                        </div>
                                                        {/* City */}
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">City *</label>
                                                            <input className="form-control" name="city" value={address.city || ''} onChange={e => handleAddressChange(index, e)} required />
                                                            <span className="text-danger">{currentPartyError?.addresses?.[index]?.city}</span>
                                                        </div>
                                                        {/* State */}
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">State *</label>
                                                            {/* TODO: Replace with State Dropdown */}
                                                            <input className="form-control" name="state" value={address.state || ''} onChange={e => handleAddressChange(index, e)} required />
                                                            <span className="text-danger">{currentPartyError?.addresses?.[index]?.state}</span>
                                                        </div>
                                                        {/* Pincode */}
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">Pincode *</label>
                                                            <input className="form-control" name="pincode" value={address.pincode || ''} onChange={e => handleAddressChange(index, e)} required maxLength="6" />
                                                            <span className="text-danger">{currentPartyError?.addresses?.[index]?.pincode}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Action Buttons */}
                                            <div className="col-12 mt-4">
                                                <div className="text-end">
                                                    {formType === 'create' ? (
                                                        <>
                                                            <button className="btn btn-outline-primary me-2 btn-sm" type="button" onClick={() => saveForm(false)} disabled={loading}>Save + Next</button>
                                                            <button className="btn btn-primary me-2 btn-sm" type="button" onClick={() => saveForm(true)} disabled={loading}>Save + Exit</button>
                                                            <button className="d-none d-md-inline-block btn btn-danger btn-sm me-2" type="button" onClick={handleReset} disabled={loading}>Reset</button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button className="btn btn-primary me-2 btn-sm" type="button" onClick={() => saveForm(true)} disabled={loading}>Update</button>
                                                            <button className="btn btn-secondary me-2 btn-sm" type="button" onClick={handleReset} disabled={loading}>Reset</button>
                                                           
                                                        </>
                                                    )}
                                                    <button className="d-none  d-md-inline-block btn btn-light-reddit b-r-22 d-inline-flex-center" type="button" onClick={() => navigate('/franchisee_user/masters/parties')} disabled={loading}>Go Back</button>

                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </>
    );
};

export default PartiesForm;